import React, { useMemo } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import * as Yup from 'yup';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const VALORES_INICIAIS = {
  ge: null,
  grupoGe: null,
  cdias: 30,
  cvez: 2,
  codigoPortador: null,
  codigoCedente: null,
  ordem: 'Cedente',
};

const validationSchema = Yup.object({
  ge: Yup.number().nullable(),
  grupoGe: Yup.number().nullable(),
  cdias: Yup.number()
    .typeError('Qtde. de dias inválida')
    .min(1, 'Qtde. de dias inválida')
    .required('Qtde. de dias inválida'),
  cvez: Yup.number()
    .typeError('Qtde. de vezes inválida')
    .min(0, 'Qtde. de vezes inválida')
    .required('Qtde. de vezes inválida'),
  codigoPortador: Yup.number().nullable(),
  codigoCedente: Yup.number().nullable(),
  ordem: Yup.string()
    .oneOf(['Cedente', 'Portador'])
    .required(),
}).test(
  'ge-mutex-grupoge',
  'Selecione G.E. ou Grupo de G.E., não ambos.',
  (vals) => !(vals.ge && vals.grupoGe)
);

const FiltrosChequesProrrogados = ({
  gruposEmpresa,
  gruposGe,
  contasGe,
  carregandoContas,
  onGeChange,
  onSubmit,
  onCancelar,
  submitting,
}) => {
  // Backend retorna snake_case (ver .run/MEMORY.md §Contratos DTO).
  const opcoesGe = useMemo(
    () =>
      (gruposEmpresa || []).map((item) => ({
        value: item.codigo_grupo_empresa,
        label: `${item.codigo_grupo_empresa} - ${item.apelido}`,
      })),
    [gruposEmpresa]
  );

  const opcoesGrupoGe = useMemo(
    () =>
      (gruposGe || []).map((item) => ({
        value: item.codigo,
        label: `${item.codigo} - ${item.descricao}`,
      })),
    [gruposGe]
  );

  const opcoesContas = useMemo(
    () =>
      (contasGe || []).map((item) => ({
        value: item.codigo_conta,
        label: `${item.codigo_conta} - ${item.apelido_conta}`,
      })),
    [contasGe]
  );

  return (
    <Formik
      initialValues={VALORES_INICIAIS}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // Backend serializa via SnakeCaseStrategy (ver .run/MEMORY.md §Contratos DTO).
        // ordem é enum numérico no wire: 0 = Cedente, 1 = Portador.
        onSubmit({
          ge: values.ge,
          grupo_ge: values.grupoGe,
          cdias: values.cdias,
          cvez: values.cvez,
          codigo_portador: values.codigoPortador,
          codigo_cedente: values.codigoCedente,
          ordem: values.ordem === 'Portador' ? 1 : 0,
        });
      }}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        handleSubmit,
        handleChange,
        handleBlur,
        resetForm,
      }) => {
        const erroMutex =
          (touched.ge || touched.grupoGe) &&
          values.ge &&
          values.grupoGe
            ? 'Selecione G.E. ou Grupo de G.E., não ambos.'
            : null;

        const valorSelecionado = (opts, val) =>
          opts.find((o) => o.value === val) || null;

        const handleGeSelect = (selecionado) => {
          const novoValor = selecionado ? selecionado.value : null;
          setFieldValue('ge', novoValor);
          setFieldValue('grupoGe', null);
          setFieldValue('codigoPortador', null);
          setFieldValue('codigoCedente', null);
          setFieldTouched('ge', true, false);
          onGeChange(novoValor);
        };

        const handleGrupoGeSelect = (selecionado) => {
          const novoValor = selecionado ? selecionado.value : null;
          setFieldValue('grupoGe', novoValor);
          setFieldValue('ge', null);
          setFieldValue('codigoPortador', null);
          setFieldValue('codigoCedente', null);
          setFieldTouched('grupoGe', true, false);
          onGeChange(null);
        };

        const handleResetar = () => {
          resetForm({ values: VALORES_INICIAIS });
          onCancelar();
        };

        return (
          <Card className="mb-3 shadow-sm">
            <Card.Header className="bg-light">
              <strong>Filtros</strong>
            </Card.Header>
            <Card.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">
                      Grupo de Empresa (G.E.)
                    </Form.Label>
                    <Select
                      placeholder="Selecione..."
                      isClearable
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesGe}
                      value={valorSelecionado(opcoesGe, values.ge)}
                      onChange={handleGeSelect}
                      noOptionsMessage={() => 'Nenhuma GE disponível'}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">
                      Grupo de G.E.
                    </Form.Label>
                    <Select
                      placeholder="Selecione..."
                      isClearable
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesGrupoGe}
                      value={valorSelecionado(opcoesGrupoGe, values.grupoGe)}
                      onChange={handleGrupoGeSelect}
                      noOptionsMessage={() =>
                        'Nenhum Grupo de G.E. disponível'
                      }
                    />
                    {erroMutex && (
                      <Form.Text className="text-danger">
                        {erroMutex}
                      </Form.Text>
                    )}
                  </Col>
                  <Col xs={6} md={3} lg={2} className="mb-3">
                    <Form.Label className="small mb-1">
                      Qtde. de dias
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      step={1}
                      name="cdias"
                      value={values.cdias}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.cdias && !!errors.cdias}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cdias}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs={6} md={3} lg={2} className="mb-3">
                    <Form.Label className="small mb-1">
                      Qtde. de vezes
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1}
                      name="cvez"
                      value={values.cvez}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.cvez && !!errors.cvez}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cvez}
                    </Form.Control.Feedback>
                    <Form.Text className="text-danger">
                      Para não utilizar o filtro de Qtde. vezes informe zero
                      (0).
                    </Form.Text>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">Portador</Form.Label>
                    <Select
                      placeholder={
                        values.ge
                          ? 'Selecione...'
                          : 'Selecione uma G.E. primeiro'
                      }
                      isClearable
                      isDisabled={!values.ge || carregandoContas}
                      isLoading={carregandoContas}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesContas}
                      value={valorSelecionado(
                        opcoesContas,
                        values.codigoPortador
                      )}
                      onChange={(selecionado) =>
                        setFieldValue(
                          'codigoPortador',
                          selecionado ? selecionado.value : null
                        )
                      }
                      noOptionsMessage={() => 'Nenhum portador disponível'}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4} className="mb-3">
                    <Form.Label className="small mb-1">Cedente</Form.Label>
                    <Select
                      placeholder={
                        values.ge
                          ? 'Selecione...'
                          : 'Selecione uma G.E. primeiro'
                      }
                      isClearable
                      isDisabled={!values.ge || carregandoContas}
                      isLoading={carregandoContas}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      options={opcoesContas}
                      value={valorSelecionado(
                        opcoesContas,
                        values.codigoCedente
                      )}
                      onChange={(selecionado) =>
                        setFieldValue(
                          'codigoCedente',
                          selecionado ? selecionado.value : null
                        )
                      }
                      noOptionsMessage={() => 'Nenhum cedente disponível'}
                    />
                  </Col>
                  <Col xs={12} md={12} lg={4} className="mb-3">
                    <Form.Label className="small mb-1 d-block">
                      Ordem
                    </Form.Label>
                    <Form.Check
                      inline
                      type="radio"
                      id="ordem-cedente"
                      name="ordem"
                      label="Cedente"
                      value="Cedente"
                      checked={values.ordem === 'Cedente'}
                      onChange={() => setFieldValue('ordem', 'Cedente')}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="ordem-portador"
                      name="ordem"
                      label="Portador"
                      value="Portador"
                      checked={values.ordem === 'Portador'}
                      onChange={() => setFieldValue('ordem', 'Portador')}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Button
                      type="button"
                      variant="secondary"
                      className="mr-2"
                      onClick={handleResetar}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting || !!erroMutex}
                    >
                      {submitting ? 'Gerando...' : 'OK'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        );
      }}
    </Formik>
  );
};

export default FiltrosChequesProrrogados;
